import {
  CmsContentFooterSchema,
  type CmsContentFooterForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const FOOTER_LOGO_KEY_PREFIX = "uploads/cms/footer";

type FooterFormProps = {
  content: CmsContentFooterForm | null;
  onSubmit: (content: CmsContentFooterForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultFooter: CmsContentFooterForm = {
  logoKey: "",
  brandName: "",
  slogan: {},
  address: {},
  phone: "",
  email: "",
  socialLinks: [{}],
};

export function FooterForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: FooterFormProps) {
  const defaultValues: CmsContentFooterForm = content
    ? {
        logoKey: content.logoKey ?? "",
        brandName: content.brandName ?? "",
        slogan: content.slogan ?? {},
        address: content.address ?? {},
        phone: content.phone ?? "",
        email: content.email ?? "",
        socialLinks:
          Array.isArray(content.socialLinks) && content.socialLinks.length > 0
            ? content.socialLinks
            : [{}],
      }
    : defaultFooter;

  const methods = RHF.useForm<CmsContentFooterForm>({
    resolver: zodResolver(
      CmsContentFooterSchema,
    ) as Resolver<CmsContentFooterForm>,
    defaultValues,
  });

  const setValue = methods.setValue;
  const watchLogoKey = methods.watch("logoKey") ?? "";
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = useCallback(
    async (vals: CmsContentFooterForm) => {
      const data = { ...vals };
      if (pendingLogoFile) {
        setUploading(true);
        try {
          data.logoKey = await uploadImageToKey(
            FOOTER_LOGO_KEY_PREFIX,
            pendingLogoFile,
          );
        } finally {
          setUploading(false);
        }
      }
      onSubmit(data);
    },
    [pendingLogoFile, onSubmit],
  );

  return (
    <FormRoot<CmsContentFooterForm>
      methods={methods as UseFormReturn<CmsContentFooterForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-8">
        {/* Logo & Brand */}
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h3 className="border-b pb-2 font-semibold text-muted-foreground text-sm">
            Logo ແລະ ຊື່ຍີ່ຫໍ້
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <ImageKeyUploadField
              keyPrefix={FOOTER_LOGO_KEY_PREFIX}
              label="Logo (1:1)"
              aspectHint="ອັດສ່ວນ 1:1"
              aspectRatio="aspect-square"
              widthPx={200}
              heightPx={200}
              value={watchLogoKey}
              onChange={(key) =>
                setValue("logoKey", key, { shouldDirty: true })
              }
              disabled={submitting}
              deferUpload
              pendingFile={pendingLogoFile}
              onFileSelect={setPendingLogoFile}
              onClear={() => {
                setValue("logoKey", "", { shouldDirty: true });
                setPendingLogoFile(null);
              }}
            />
            <div className="flex flex-col justify-center">
              <FormInput
                name="brandName"
                label="ຊື່ຍີ່ຫໍ້"
                placeholder="ຊື່ບໍລິສັດ ຫຼື ຍີ່ຫໍ້"
              />
            </div>
          </div>
        </section>

        {/* Slogan */}
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h3 className="border-b pb-2 font-semibold text-muted-foreground text-sm">
            Slogan
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormInput
              name="slogan.lo"
              label="Slogan (ລາວ)"
              placeholder="ຄຳຂວັນ ຫຼື ສະຫຼຸບຍີ່ຫໍ້"
            />
            <FormInput
              name="slogan.th"
              label="Slogan (ໄທ)"
              placeholder="คำขวัญ หรือ สรุปแบรนด์"
            />
            <FormInput
              name="slogan.en"
              label="Slogan (EN)"
              placeholder="Tagline or brand summary"
            />
          </div>
        </section>

        {/* Address */}
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h3 className="border-b pb-2 font-semibold text-muted-foreground text-sm">
            ທີ່ຢູ່
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormInput
              name="address.lo"
              label="ທີ່ຢູ່ (ລາວ)"
              placeholder="ບ້ານ, ເມືອງ, ແຂວງ"
            />
            <FormInput
              name="address.th"
              label="ที่อยู่ (ໄທ)"
              placeholder="ตำบล, อำเภอ, จังหวัด"
            />
            <FormInput
              name="address.en"
              label="Address (EN)"
              placeholder="Street, district, province"
            />
          </div>
        </section>

        {/* Contact */}
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h3 className="border-b pb-2 font-semibold text-muted-foreground text-sm">
            ຕິດຕໍ່
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput name="phone" label="ເບີໂທ" placeholder="020 xxx xxxxx" />
            <FormInput
              name="email"
              label="ອີເມວ"
              type="email"
              placeholder="contact@example.com"
            />
          </div>
        </section>

        {/* Social links */}
        <section className="space-y-4 rounded-xl border bg-card p-5">
          <h3 className="border-b pb-2 font-semibold text-muted-foreground text-sm">
            Social links
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormInput
              name="socialLinks.0.facebook"
              label="Facebook URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.instagram"
              label="Instagram URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.tiktok"
              label="TikTok URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.twitter"
              label="Twitter URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.youtube"
              label="YouTube URL"
              placeholder="https://..."
            />
            <FormInput
              name="socialLinks.0.other"
              label="Other URL"
              placeholder="https://..."
            />
          </div>
        </section>

        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດຮູບ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
